// src/admin/PageContentList.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import PageContentEditor from "./PageContentEditor";

export default function PageContentList({
  baseURL = "https://gantasoft.in/rankridgeschool/dev/v1",
  apiKey = "12345678",
  pageId,        // optional filter
  subpageId,     // optional filter
}) {
  const api = useMemo(() => axios.create({
    baseURL, headers: { "X-API-Key": apiKey },
  }), [baseURL, apiKey]);

  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editorItem, setEditorItem] = useState(null); // open editor modal

  async function load() {
    setLoading(true);
    try {
      const params = {
        q, status, page, limit,
        sort: "page_content_id", order: "DESC",
      };
      if (pageId) params.page_id = pageId;
      if (subpageId) params.subpage_id = subpageId;

      const { data } = await api.get("/page_content");
      setRows(data?.items || []);
      setTotal(data?.pagination?.total || 0);
      setPages(data?.pagination?.pages || 0);
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Load failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status, page, pageId, subpageId]);

  const toggleStatus = async (item) => {
    const next = item.status === "active" ? "inactive" : "active";
    try {
      await api.patch(`/page_content/${item.page_content_id}`, {
        field: "status", value: next
      });
      setRows((arr) => arr.map(r => r.page_content_id === item.page_content_id ? { ...r, status: next } : r));
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Status change failed");
    }
  };

  const destroy = async (item) => {
    if (!window.confirm("Delete this page content?")) return;
    try {
      await api.delete(`/page_content/${item.page_content_id}`);
      setRows(arr => arr.filter(r => r.page_content_id !== item.page_content_id));
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Delete failed");
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="flex flex-wrap items-end gap-2 justify-between">
        <h2 className="text-xl font-semibold">Page Content</h2>
        <button
          onClick={() => setEditorItem({ page_id: pageId || null, subpage_id: subpageId || null, h1_main_title: "", carousel_heading: "", subdescription: "", status: "active" })}
          className="px-3 py-1.5 bg-green-700 text-white rounded"
        >
          + Add Content
        </button>
      </div>

      <div className="mt-4 grid md:grid-cols-4 gap-2">
        <input
          placeholder="Search title/heading/text..."
          className="border rounded px-3 py-2"
          value={q}
          onChange={(e) => { setPage(1); setQ(e.target.value); }}
        />
        <select
          className="border rounded px-3 py-2"
          value={status}
          onChange={(e) => { setPage(1); setStatus(e.target.value); }}
        >
          <option value="">All statuses</option>
          <option value="active">active</option>
          <option value="inactive">inactive</option>
          <option value="deleted">deleted</option>
        </select>
      </div>

      {loading ? (
        <div className="mt-4 text-gray-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="mt-4 text-gray-500">No results.</div>
      ) : (
        <div className="mt-4 grid gap-3">
          {rows.map((it) => (
            <div key={it.page_content_id} className="border rounded p-3 bg-white">
              <div className="flex items-center justify-between">
                <div className="font-medium">{it.h1_main_title}</div>
                <span className="text-xs px-2 py-0.5 rounded bg-gray-100">{it.status}</span>
              </div>
              {it.carousel_heading && (
                <div className="text-sm text-gray-700">{it.carousel_heading}</div>
              )}
              {it.subdescription && (
                <div className="text-xs text-gray-600 mt-1 line-clamp-2">{it.subdescription}</div>
              )}
              <div className="text-xs text-gray-500 mt-2">
                ID: {it.page_content_id} · Page: {it.page_id ?? '-'} · Subpage: {it.subpage_id ?? '-'}
              </div>

              <div className="flex gap-2 mt-2">
                <button onClick={() => toggleStatus(it)} className="px-2 py-1 text-xs rounded bg-gray-800 text-white">
                  {it.status === "active" ? "Deactivate" : "Activate"}
                </button>
                <button onClick={() => setEditorItem(it)} className="px-2 py-1 text-xs rounded bg-blue-600 text-white">
                  Edit
                </button>
                <button onClick={() => destroy(it)} className="px-2 py-1 text-xs rounded bg-red-600 text-white">
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="mt-4 flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1.5 rounded bg-gray-200 disabled:opacity-50"
          >
            Prev
          </button>
          <div className="text-sm">Page {page} of {pages} · {total} total</div>
          <button
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            disabled={page >= pages}
            className="px-3 py-1.5 rounded bg-gray-200 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {editorItem && (
        <PageContentEditor
          baseURL={baseURL}
          apiKey={apiKey}
          initial={editorItem}
          onClose={() => setEditorItem(null)}
          onSaved={() => { setEditorItem(null); load(); }}
        />
      )}
    </div>
  );
}
